<template>
  <div class="flex flex-col gap-6 p-6 max-w-4xl w-full">
    <!-- Header -->
    <div>
      <h1 class="text-lg font-semibold text-n-slate-12">Chaves LLM</h1>
      <p class="text-sm text-n-slate-11 mt-0.5">
        Configure as chaves de API dos provedores de IA. Adicione múltiplas chaves
        para rotatividade automática e configure a ordem de fallback.
      </p>
    </div>

    <!-- Provider padrão + Modelo -->
    <div class="rounded-xl bg-n-solid-2 border border-n-weak p-5 flex flex-col gap-4">
      <h2 class="text-sm font-semibold text-n-slate-12">Configuração Padrão</h2>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Provider Padrão</label>
          <select
            v-model="form.llm_default_provider"
            class="w-full px-3 py-2 text-sm bg-n-background border border-n-weak rounded-lg text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option v-for="p in providers" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">Modelo Padrão</label>
          <select
            v-model="form.llm_default_model"
            class="w-full px-3 py-2 text-sm bg-n-background border border-n-weak rounded-lg text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option v-for="m in availableModels" :key="m" :value="m">{{ m }}</option>
          </select>
        </div>
      </div>

      <!-- Fallback order -->
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          Ordem de Fallback
          <span class="font-normal text-n-slate-10">(se o provider padrão falhar)</span>
        </label>
        <div class="flex flex-wrap gap-2 mt-1">
          <div
            v-for="(fb, idx) in form.llm_fallback_order"
            :key="fb"
            class="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-n-background border border-n-weak text-xs text-n-slate-12"
          >
            <span class="text-[10px] text-n-slate-10 font-mono">{{ idx + 1 }}.</span>
            <span>{{ providerLabel(fb) }}</span>
            <button
              class="ml-1 text-n-slate-10 hover:text-red-500 text-xs"
              @click="removeFallback(idx)"
            >×</button>
          </div>
          <select
            v-if="availableFallbacks.length"
            class="px-2 py-1 text-xs bg-n-background border border-dashed border-n-weak rounded-lg text-n-slate-10 outline-none focus:border-n-brand"
            @change="addFallback($event)"
          >
            <option value="">+ Adicionar</option>
            <option v-for="p in availableFallbacks" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </div>
        <p class="text-[10px] text-n-slate-10 mt-1">
          Se o provider padrão retornar erro, tenta o próximo na lista automaticamente.
        </p>
      </div>
    </div>

    <!-- Key pools per provider -->
    <div
      v-for="provider in providers"
      :key="provider.value"
      class="rounded-xl bg-n-solid-2 border border-n-weak p-5 flex flex-col gap-3"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h2 class="text-sm font-semibold text-n-slate-12">{{ provider.label }}</h2>
          <span
            v-if="keyCount(provider.value) > 0"
            class="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
          >
            {{ keyCount(provider.value) }} {{ keyCount(provider.value) === 1 ? 'chave' : 'chaves' }}
          </span>
          <span
            v-else
            class="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-n-alpha-2 text-n-slate-10"
          >
            sem chaves
          </span>
        </div>
        <span
          v-if="form.llm_default_provider === provider.value"
          class="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-n-brand/10 text-n-brand"
        >
          PADRÃO
        </span>
      </div>

      <!-- Keys textarea -->
      <div>
        <label class="block text-[10px] font-medium text-n-slate-10 mb-1">
          Cole as chaves abaixo (uma por linha)
        </label>
        <textarea
          :value="getKeysText(provider.value)"
          :placeholder="provider.placeholder"
          rows="3"
          class="w-full px-3 py-2 text-xs bg-n-background border border-n-weak rounded-lg text-n-slate-12 placeholder-n-slate-10 outline-none focus:border-n-brand font-mono resize-y"
          @input="setKeysFromText(provider.value, $event.target.value)"
        />
        <p class="text-[10px] text-n-slate-10 mt-0.5">{{ provider.description }}</p>
      </div>
    </div>

    <!-- Info box -->
    <div class="rounded-lg bg-n-brand/5 border border-n-brand/20 p-3">
      <p class="text-xs text-n-slate-11">
        <strong class="text-n-brand">Como funciona:</strong>
        A cada chamada à IA, o sistema usa a próxima chave do pool (round-robin).
        Se uma chave retornar erro 429 (rate limit) ou 403 (quota), automaticamente
        tenta a próxima. Se todas as chaves do provider falharem, tenta o próximo
        provider na ordem de fallback.
      </p>
    </div>

    <!-- Save -->
    <div class="flex items-center gap-3">
      <button
        class="px-4 py-2 text-xs font-semibold text-white rounded-lg bg-n-brand hover:opacity-90 transition-opacity disabled:opacity-50"
        :disabled="saving"
        @click="save"
      >
        {{ saving ? 'Salvando...' : 'Salvar Configuração' }}
      </button>
      <span v-if="saved" class="text-xs text-green-600 font-medium">Salvo com sucesso!</span>
      <span v-if="error" class="text-xs text-red-500 font-medium">{{ error }}</span>
    </div>
  </div>
</template>

<script>
/* global axios */
export default {
  data() {
    return {
      saving: false,
      saved: false,
      error: '',
      form: {
        llm_default_provider: 'google_direct',
        llm_default_model: 'gemini-2.5-flash',
        llm_fallback_order: [],
        llm_keys_google_direct: [],
        llm_keys_openrouter: [],
        llm_keys_openai: [],
        llm_keys_anthropic: [],
      },
      providers: [
        {
          value: 'google_direct',
          label: 'Google Gemini (Direto)',
          placeholder: 'AIzaSy...\nAIzaSy...\nAIzaSy...',
          description: 'API gratuita com limite por chave. Adicione múltiplas para aumentar o throughput.',
        },
        {
          value: 'openrouter',
          label: 'OpenRouter',
          placeholder: 'sk-or-...',
          description: 'Acessa 200+ modelos (Gemini, GPT, Claude, Llama). Pago por uso.',
        },
        {
          value: 'openai',
          label: 'OpenAI',
          placeholder: 'sk-...',
          description: 'GPT-4o, GPT-4.1. Pago por uso.',
        },
        {
          value: 'anthropic',
          label: 'Anthropic',
          placeholder: 'sk-ant-...',
          description: 'Claude Sonnet, Claude Haiku. Pago por uso.',
        },
      ],
      models: {
        google_direct: ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.0-flash'],
        openrouter: [
          'google/gemini-2.5-flash', 'google/gemini-2.5-pro',
          'openai/gpt-4.1-mini', 'openai/gpt-4.1',
          'anthropic/claude-sonnet-4', 'anthropic/claude-haiku-4',
        ],
        openai: ['gpt-4.1-mini', 'gpt-4.1', 'gpt-4o', 'gpt-4o-mini'],
        anthropic: ['claude-sonnet-4-20250514', 'claude-haiku-4-20250414'],
      },
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    apiUrl() {
      return `/api/v1/accounts/${this.accountId}`;
    },
    availableModels() {
      return this.models[this.form.llm_default_provider] || this.models.google_direct;
    },
    availableFallbacks() {
      const used = [this.form.llm_default_provider, ...this.form.llm_fallback_order];
      return this.providers.filter(p => !used.includes(p.value));
    },
  },
  mounted() {
    this.fetchAccount();
  },
  methods: {
    providerLabel(value) {
      return this.providers.find(p => p.value === value)?.label || value;
    },
    keyCount(provider) {
      return (this.form[`llm_keys_${provider}`] || []).length;
    },
    getKeysText(provider) {
      return (this.form[`llm_keys_${provider}`] || []).join('\n');
    },
    setKeysFromText(provider, text) {
      const keys = text.split('\n').map(k => k.trim()).filter(k => k.length > 5);
      // Deduplicate
      this.form[`llm_keys_${provider}`] = [...new Set(keys)];
    },
    addFallback(event) {
      const val = event.target.value;
      if (val) {
        this.form.llm_fallback_order.push(val);
        event.target.value = '';
      }
    },
    removeFallback(idx) {
      this.form.llm_fallback_order.splice(idx, 1);
    },
    async fetchAccount() {
      try {
        const { data } = await axios.get(this.apiUrl);
        const s = data.custom_attributes || {};
        if (s.llm_default_provider) this.form.llm_default_provider = s.llm_default_provider;
        if (s.llm_default_model) this.form.llm_default_model = s.llm_default_model;
        if (Array.isArray(s.llm_fallback_order)) this.form.llm_fallback_order = s.llm_fallback_order;
        if (Array.isArray(s.llm_keys_google_direct)) this.form.llm_keys_google_direct = s.llm_keys_google_direct;
        if (Array.isArray(s.llm_keys_openrouter)) this.form.llm_keys_openrouter = s.llm_keys_openrouter;
        if (Array.isArray(s.llm_keys_openai)) this.form.llm_keys_openai = s.llm_keys_openai;
        if (Array.isArray(s.llm_keys_anthropic)) this.form.llm_keys_anthropic = s.llm_keys_anthropic;
      } catch (e) {
        // defaults
      }
    },
    async save() {
      this.saving = true;
      this.saved = false;
      this.error = '';
      try {
        const { data } = await axios.get(this.apiUrl);
        const existing = data.custom_attributes || {};
        await axios.patch(this.apiUrl, {
          custom_attributes: {
            ...existing,
            llm_default_provider: this.form.llm_default_provider,
            llm_default_model: this.form.llm_default_model,
            llm_fallback_order: this.form.llm_fallback_order,
            llm_keys_google_direct: this.form.llm_keys_google_direct,
            llm_keys_openrouter: this.form.llm_keys_openrouter,
            llm_keys_openai: this.form.llm_keys_openai,
            llm_keys_anthropic: this.form.llm_keys_anthropic,
          },
        });
        this.saved = true;
        setTimeout(() => { this.saved = false; }, 3000);
      } catch (e) {
        this.error = 'Erro ao salvar. Tente novamente.';
        setTimeout(() => { this.error = ''; }, 5000);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
